import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generatePath, useHistory, useLocation, useParams } from "react-router-dom";
import { Group } from "@scrile/api-provider/dist/api/GroupsProvider";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { MessageThread } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { useTranslation } from "locales";
import useAuthUser from "hooks/useAuthUser";
import useJumpToMessage from "hooks/useJumpToMessage";
import useMessages from "hooks/useMessages";
import useMessageSearch from "hooks/useMessageSearch";
import useMessageSelection from "hooks/useMessageSelection";
import useTypingIndicator from "hooks/useTypingIndicator";
import emitter, { EVENTS } from "lib/emitter";
import gqlFetch from "lib/gqlFetch";
import { mergeMessages, oldestRealId } from "lib/mergeMessages";
import { getMessageMetadata, isOptimisticMessage } from "lib/messageMetadata";
import { getMessageSnapshot, setMessageSnapshot } from "lib/messageSnapshots";
import { cacheMessages, getCachedMessages, removeCachedMessage, cacheGroup, getCachedGroup } from "lib/offlineCache";
import providers from "lib/providers";
import { OptimisticMessage } from "types";
import ChatSearchBar from "components/ChatSearchBar";
import ChatSelectionToolbar from "components/ChatSelectionToolbar";
import ChatSkeleton from "components/ChatSkeleton";
import ForwardMessageModal from "components/ForwardMessageModal";
import GroupChatHeader from "components/GroupChatHeader";
import GroupInfoPanel from "components/GroupInfoPanel";
import MediaGalleryPanel from "components/MediaGalleryPanel";
import MessageInput from "components/MessageInput";
import MessagesListView from "components/MessagesListView";
import PinnedMessagesBar from "components/PinnedMessagesBar";
import ScheduledMessages from "components/ScheduledMessages";
import ThreadPanel from "components/ThreadPanel";
import TopicsList from "components/TopicsList";
import { Routes } from "../../../../router/config";
import "./style.scss";

interface Props {
  onBack?: () => void;
}

// Cursor-pagination page size for scroll-up history loads. Older pages are
// fetched with `lessThanId` (id-based cursor) instead of an offset `page`, so
// new messages arriving mid-scroll can't shift a page boundary and drop rows.
const PAGE_SIZE = 30;

function GroupMessagesList({ onBack }: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const { groupId, topicId } = useParams<{ groupId: string; topicId?: string }>();
  // Group Topics (Темы): the General topic is addressed by the literal "general"
  // in the URL but carries no topicId on the backend (topicId IS NULL); a numeric
  // id addresses a specific custom topic.
  const numericTopicId = topicId && topicId !== "general" ? topicId : undefined;
  // Per-topic offline cache key so a topic's thread/messages don't clobber the
  // group's General cache (and vice-versa).
  const cacheKey = topicId ? `${groupId}:${topicId}` : groupId;
  const { user: authUser } = useAuthUser();
  const [group, setGroup] = useState<Group | null>(null);
  const [topicName, setTopicName] = useState<string | undefined>(undefined);
  const [thread, setThread] = useState<MessageThread | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showMediaPanel, setShowMediaPanel] = useState(false);
  const [activeCallUuid, setActiveCallUuid] = useState<string | null>(null);
  const [highlightMentionId, setHighlightMentionId] = useState<string | undefined>(undefined);
  const [showUnread, setShowUnread] = useState(true);
  const [minUnreadMessageId, setMinUnreadMessageId] = useState<string | undefined>(undefined);
  const [searchOpen, setSearchOpen] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [forwardMessage, setForwardMessage] = useState<Message | null>(null);
  // When non-null, opens ForwardMessageModal with a BULK selection
  // (multi-select). Separate from forwardMessage so the modal can tell
  // single-message vs. selection flows apart.
  const [forwardBulk, setForwardBulk] = useState<Message[] | null>(null);
  const [threadParentMessage, setThreadParentMessage] = useState<Message | null>(null);
  const [pinnedMessages, setPinnedMessages] = useState<
    Array<{ messageId: string; text?: string; senderName?: string }>
  >([]);
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set());
  const reverseContentRef = useRef<HTMLDivElement>(null);
  const scrollToBottomFnRef = useRef<(() => void) | null>(null);
  // Set when the cache→fresh phase swap fires (inside `hydrate` flow below).
  // MessagesListView's matching settleAtBottomRef pins the viewport to bottom
  // for ~1.5 s while async height changes (images, link previews, voice
  // metadata) settle — eliminates the up-down "shake" after page reload.
  const settleAtBottomFnRef = useRef<(() => void) | null>(null);
  // Chat generation: bumped on every group/topic switch; loadMessages captures
  // it to drop pages that raced the switch.
  const loadGenRef = useRef(0);
  const { markRead } = useMessages();
  const { typingUsers, sendTyping, addTypingUser } = useTypingIndicator(thread?.id);

  const {
    searchResults,
    searchIndex,
    searchQuery,
    searchHighlightId,
    search,
    navigate: searchNavigate,
    clearSearch,
    resetSearch,
  } = useMessageSearch(thread?.id);

  // Compute thread reply counts from loaded messages
  const threadReplyCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const msg of messages) {
      const meta = getMessageMetadata(msg);
      if (meta?.threadParentId) {
        counts.set(meta.threadParentId, (counts.get(meta.threadParentId) || 0) + 1);
      }
    }
    return counts;
  }, [messages]);

  const scrollToMention = useCallback(() => {
    if (!authUser) return;
    const uid = String(authUser.id);
    const mentionPattern = `@[${uid}]`;
    const found = messages.find((m) => m.text?.includes(mentionPattern));
    if (found) {
      setHighlightMentionId(found.id);
      setTimeout(() => setHighlightMentionId(undefined), 3000);
    }
  }, [messages, authUser]);

  // Guards setState in the async loadActiveCall below: a focus/60s-interval refresh
  // can resolve after the chat is closed, and React 16 warns on setState-after-unmount.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const loadActiveCall = useCallback(
    async (groupData?: Group | null) => {
      const g = groupData || group;
      if (!g) return;
      try {
        if (!g.callsEnabled) {
          setActiveCallUuid(null);
          return;
        }
        const response = await providers.ConferenceProvider.search({ groupId: Number(g.id), active: true });
        if (!mountedRef.current) return;
        if (response.result?.length > 0) {
          setActiveCallUuid(response.result[0].uuid);
        } else {
          setActiveCallUuid(null);
        }
      } catch (e) {
        if (mountedRef.current) setActiveCallUuid(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [group]
  );

  const handleStartCall = useCallback(async () => {
    if (!group) return;
    try {
      const data = await providers.GroupsProvider.startGroupCall(group.id);
      const uuid = data?.uuid || activeCallUuid;
      if (uuid) {
        history.push(generatePath(Routes.conferenceJoinByConferenceUuid, { conferenceUuid: uuid }));
      }
    } catch (e) {
      console.error("Failed to start group call:", e);
    }
  }, [group, activeCallUuid, history]);

  const handleStartVoiceCall = useCallback(async () => {
    if (!group) return;
    try {
      const data = await providers.GroupsProvider.startGroupCall(group.id);
      const uuid = data?.uuid || activeCallUuid;
      if (uuid) {
        history.push(generatePath(Routes.conferenceJoinByConferenceUuid, { conferenceUuid: uuid }) + "?voiceOnly=1");
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to start voice call:", e);
    }
  }, [group, activeCallUuid, history]);

  const loadGroup = useCallback(async (): Promise<Group | null> => {
    if (!groupId || !authUser) return null;
    try {
      const isNumericLegacy = /^\d+$/.test(groupId);
      let loaded: Group | null = null;
      if (isNumericLegacy) {
        const groups = await providers.GroupsProvider.search({ data: { id: groupId } });
        loaded = groups[0] || null;
        if (loaded?.slug) {
          // Preserve the topic segment when normalizing a numeric group id to its
          // slug, otherwise a topic deep-link bounces to the topics overview.
          // Router state (e.g. a jump-to-message target) is carried across the
          // alias redirect — see useJumpToMessage's invalidation rule.
          history.replace(
            topicId
              ? generatePath(Routes.groupMessageTopic, { groupId: loaded.slug, topicId })
              : generatePath(Routes.groupMessages, { groupId: loaded.slug }),
            history.location.state
          );
        }
      } else {
        loaded = await providers.GroupsProvider.findBySlug(groupId);
      }
      if (loaded) setGroup(loaded);
      return loaded;
    } catch (e) {
      console.error("Failed to load group:", e);
    }
    return null;
  }, [groupId, topicId, authUser, history]);

  const loadThread = useCallback(
    async (groupData: Group | null) => {
      if (!authUser || !groupData) return null;
      try {
        const result = await providers.MessageThreadsProvider.find({
          // topicId undefined → the group's General thread (topicId IS NULL);
          // a numeric topicId → that topic's dedicated thread.
          data: {
            userId: authUser.id,
            subjectType: "group",
            subjectId: groupData.id,
            topicId: numericTopicId,
            size: 1,
          },
        });
        const found = result.result?.[0] || null;
        if (found) {
          setThread(found);
          return found;
        }
      } catch (e) {
        console.error("Failed to find group thread:", e);
      }
      return null;
    },
    [authUser, numericTopicId]
  );

  // `beforeId` undefined → newest page; a numeric id → older page strictly
  // before that id (cursor pagination, immune to offset drift from new
  // messages arriving mid-scroll).
  const loadMessages = useCallback(async (threadId: string, beforeId?: string): Promise<Message[]> => {
    // Captured chat generation: a page that raced a group/topic switch must
    // not merge into the CURRENT chat's list (mergeMessages keeps
    // out-of-window rows, so a stale merge would plant foreign messages).
    const gen = loadGenRef.current;
    try {
      const response = await providers.MessagesProvider.find({
        data: { threadId, sort: "TIME,DESC", size: PAGE_SIZE, ...(beforeId ? { lessThanId: beforeId } : {}) },
      });
      if (response.result.length > 0) {
        // Keyed by the FETCHED thread id — safe regardless of chat switches.
        cacheMessages(String(threadId), response.result).catch(() => {});
      }
      if (gen !== loadGenRef.current) return [];
      // Merge the fresh page into what's on screen (cache or a prior page)
      // instead of replacing — keeps scroll/keys stable, no flicker.
      let merged: Message[] = [];
      setMessages((prev) => {
        merged = mergeMessages(prev, response.result);
        return merged;
      });
      setHasMore(response.hasNextPage);
      return merged;
    } catch (e) {
      // Offline fallback for the newest page
      if (!navigator.onLine && !beforeId && gen === loadGenRef.current) {
        const cached = await getCachedMessages(String(threadId));
        if (cached.length > 0 && gen === loadGenRef.current) {
          const desc = [...cached].reverse(); // cache is ASC → state is DESC
          setMessages(desc as Message[]);
          setHasMore(false);
          return desc as Message[];
        }
      }
      console.error("Failed to load messages:", e);
      return [];
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    // Invalidate any in-flight page fetches of the previous group/topic.
    loadGenRef.current += 1;
    resetSearch();
    setMessages([]);
    setThread(null);
    setGroup(null);
    setHasMore(false);
    setLoading(true);
    // Close any open thread panel — otherwise it remounts with the PREVIOUS
    // chat's parent message against the new thread id and shows a stale/empty
    // panel until you switch back.
    setThreadParentMessage(null);

    // Tracks whether `hydrate` painted cached messages first. If so, the
    // subsequent fresh-data swap inside `init` triggers settleAtBottomRef so
    // MessagesListView pins the viewport to bottom across the swap.
    let cacheRendered = false;

    // Cache-first paint: render the group header + last messages from cache
    // immediately so opening a group doesn't wait on 2–3 network round-trips.
    const hydrate = async () => {
      try {
        const cached = await getCachedGroup(cacheKey);
        if (!cached || !mounted) return;
        setGroup(cached.group);
        // Topics overview mode (topics enabled, no topic selected): only the
        // group is needed — the TopicsList renders instead of a message list.
        if (cached.group?.topicsEnabled && !topicId) {
          setLoading(false);
          return;
        }
        if (cached.thread) setThread(cached.thread);
        const tid = cached.thread?.id;
        if (tid) {
          const snap = getMessageSnapshot(tid);
          const cachedMsgs = snap && snap.length ? snap : [...(await getCachedMessages(String(tid)))].reverse();
          if (mounted && cachedMsgs.length) {
            setMessages(cachedMsgs as Message[]);
            cacheRendered = true;
            setLoading(false); // skip the full-screen skeleton — show the cached chat
          }
        }
      } catch {
        /* no cache — fall through to the network init */
      }
    };

    const init = async () => {
      const loadedGroup = await loadGroup();
      // If group was deleted, redirect — can't hide thread without numeric id
      if (!loadedGroup && mounted) {
        emitter.emit(EVENTS.THREADS_REFRESH);
        if (mounted) {
          setLoading(false);
          history.replace("/messages");
        }
        return;
      }
      loadActiveCall(loadedGroup);
      // Topics overview mode: a topics-enabled group opened without a specific
      // topic shows the TopicsList overview, not a chat. Skip thread/message load.
      if (loadedGroup?.topicsEnabled && !topicId) {
        if (mounted) setLoading(false);
        return;
      }
      let foundThread = await loadThread(loadedGroup);
      if (!foundThread && mounted) {
        // Exponential backoff (250/500/1000 ms = 1.75 s total) instead of
        // the previous flat 3 × 1000 ms (3 s). Most transient failures
        // resolve within ~300 ms, so the first short delay shaves 750 ms
        // off the typical retry. Worst case still bounded under 2 s.
        const delays = [250, 500, 1000];
        for (let i = 0; i < delays.length && !foundThread && mounted; i++) {
          await new Promise((r) => setTimeout(r, delays[i]));
          foundThread = await loadThread(loadedGroup);
        }
      }
      // A specific topic whose thread can't be resolved after retries was deleted
      // or is inaccessible — bounce to the topics overview instead of showing a
      // broken empty chat. (General/"general" always resolves; topics-disabled
      // groups have no numericTopicId, so this only fires for real custom topics.)
      if (!foundThread && numericTopicId && loadedGroup && mounted) {
        setLoading(false);
        history.replace(generatePath(Routes.groupMessages, { groupId: loadedGroup.slug || loadedGroup.id }));
        return;
      }
      // Persist group + thread so the next open paints instantly from cache.
      if (loadedGroup && mounted) cacheGroup(cacheKey, loadedGroup, foundThread).catch(() => {});
      if (foundThread && mounted) {
        const loadedMsgs = await loadMessages(foundThread.id);
        // Cache→fresh swap just landed. If hydrate rendered cached messages
        // first, pin the viewport to bottom across the swap (settleAtBottomRef
        // in MessagesListView runs a 1.5 s aggressive watcher window that
        // absorbs async image / link-preview / voice-metadata height changes).
        // Skipped for cold open (no cache) — initialTopMostItemIndex handles
        // that case, and the watcher's normal grace is enough.
        if (cacheRendered && loadedMsgs.length > 0) {
          settleAtBottomFnRef.current?.();
        }
        const rawUnreadId = foundThread.minUnreadMessageId;

        let actualUnreadId: string | undefined;
        if (rawUnreadId && rawUnreadId !== "0") {
          const sorted = [...loadedMsgs].sort((a, b) => Number(a.id) - Number(b.id));
          const first = sorted.find((m) => Number(m.id) >= Number(rawUnreadId));
          if (first) actualUnreadId = first.id;
        }

        if (actualUnreadId) {
          setMinUnreadMessageId(actualUnreadId);
          setShowUnread(true);
          requestAnimationFrame(() => {
            setTimeout(() => {
              const marker = document.querySelector(".messages-list-view__new-message");
              if (marker) {
                marker.scrollIntoView({ block: "center" });
              } else {
                scrollToBottom();
              }
            }, 150);
          });
        } else {
          scrollToBottom();
        }
        // Auto-mark-read when chat is opened and all messages fit on screen
        if (loadedMsgs.length > 0) {
          const newest = loadedMsgs.reduce((a: Message, b: Message) => (Number(a.id) > Number(b.id) ? a : b));
          if (!actualUnreadId) {
            if (newest) markRead(newest).catch((e) => console.warn("[markRead] failed:", e));
          } else {
            setTimeout(() => {
              const el = reverseContentRef.current;
              if (el) {
                const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
                if (distFromBottom < 200 && newest) {
                  lastMarkedIdRef.current = newest.id;
                  markRead(newest).catch((e) => console.warn("[markRead] failed:", e));
                  setShowUnread(false);
                  setMinUnreadMessageId(undefined);
                }
              }
            }, 500);
          }
        }
      }
      if (mounted) setLoading(false);
    };
    hydrate().finally(() => {
      if (mounted) init();
    });

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, topicId, authUser?.id]);

  // Resolve the current topic's display name for the header. "general" → the
  // localized "General" label; a numeric id → the topic's name.
  useEffect(() => {
    let active = true;
    if (!topicId) {
      setTopicName(undefined);
      return;
    }
    if (!numericTopicId) {
      setTopicName(t("General"));
      return;
    }
    if (!group) return;
    (async () => {
      try {
        const topics = await providers.TopicsProvider.search(group.id);
        const match = topics.find((tp) => String(tp.id) === String(numericTopicId));
        if (active) setTopicName(match?.name || t("Topic"));
      } catch {
        if (active) setTopicName(t("Topic"));
      }
    })();
    return () => {
      active = false;
    };
  }, [topicId, numericTopicId, group, t]);

  // Keep the in-memory snapshot fresh so reopening this group within the session
  // paints instantly (covers live messages, edits, deletes, optimistic swaps).
  useEffect(() => {
    if (thread?.id && messages.length) setMessageSnapshot(thread.id, messages);
  }, [messages, thread?.id]);

  const threadRef = useRef<MessageThread | null>(null);
  threadRef.current = thread;
  const authUserRef = useRef(authUser);
  authUserRef.current = authUser;

  const isNearBottom = useCallback(() => {
    const el = reverseContentRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  }, []);

  const scrollToBottom = useCallback(() => {
    if (scrollToBottomFnRef.current) {
      scrollToBottomFnRef.current();
      return;
    }
    const el = reverseContentRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const lastMarkedIdRef = useRef<string | null>(null);
  useEffect(() => {
    // Reset on topic switch too (not just group switch) — otherwise the prior
    // topic's marked id leaks into the new topic and can skip mark-read.
    lastMarkedIdRef.current = null;
  }, [groupId, topicId]);

  // Don't carry the info/media panel across topic<->overview navigation, so e.g.
  // pressing back from a topic never lands on the overview with settings open.
  useEffect(() => {
    setShowInfoPanel(false);
    setShowMediaPanel(false);
  }, [topicId]);

  // Pre-compute newest real message to avoid O(n) work in every scroll event
  const newestRealMessage = useMemo(() => {
    const real = messages.filter((m) => !String(m.id).startsWith("_opt_"));
    if (real.length === 0) return null;
    return real.reduce((a, b) => (Number(a.id) > Number(b.id) ? a : b));
  }, [messages]);

  const newestRealMessageRef = useRef(newestRealMessage);
  newestRealMessageRef.current = newestRealMessage;

  useEffect(() => {
    const el = reverseContentRef.current;
    if (!el || !thread) return;

    let scrollTicking = false;
    const handleScroll = () => {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(() => {
        scrollTicking = false;
        const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (distFromBottom < 200) {
          const newest = newestRealMessageRef.current;
          if (newest && newest.id !== lastMarkedIdRef.current) {
            lastMarkedIdRef.current = newest.id;
            markRead(newest).catch((e) => console.warn("[markRead] failed:", e));
            setShowUnread(false);
            setMinUnreadMessageId(undefined);
          }
        }
      });
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [thread, markRead, groupId]);

  const loadActiveCallRef = useRef(loadActiveCall);
  loadActiveCallRef.current = loadActiveCall;

  // Re-check the active-call status without a manual refresh. A crash / non-owner
  // leave / owner closing the tab posts NO "call ended" system message, so the
  // badge would otherwise linger until the backend safety-net clears the flag and
  // something else triggers a refetch. Re-fetch when the tab regains focus and on a
  // light 60s interval (matching the thread-list poll cadence) so the badge clears
  // on its own once everyone has left.
  useEffect(() => {
    if (!groupId) return;
    const refresh = () => loadActiveCallRef.current();
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", refresh);
    const interval = setInterval(refresh, 60000);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", refresh);
      clearInterval(interval);
    };
  }, [groupId]);

  useEffect(() => {
    const onNewMessage = (message?: Message) => {
      const currentThread = threadRef.current;
      if (!currentThread) return;
      if (message && message.threadId && String(message.threadId) !== String(currentThread.id)) return;
      const nearBottom = isNearBottom();

      // A "call started / ended / cancelled" system message changes whether
      // the group has an active call — re-fetch the active-call UUID so the
      // Join button appears/disappears without needing a page refresh.
      if (message?.text) {
        const lower = message.text.toLowerCase();
        if (
          lower.includes("call started") ||
          lower.includes("call ended") ||
          lower.includes("call cancelled") ||
          lower.includes("missed call")
        ) {
          loadActiveCallRef.current();
        }
      }

      // Optimistic messages: add directly to state for instant display.
      // Prepend so the newest message stays at index 0 in the DESC-sorted array,
      // which after reversal in MessagesListView lands at the END (visible bottom).
      if (message && isOptimisticMessage(message)) {
        setMessages((prev) => {
          if (prev.find((m) => isOptimisticMessage(m) && m._optimisticId === message._optimisticId)) return prev;
          return [message, ...prev];
        });
        // Own optimistic message — scroll after Virtuoso processes the update
        setTimeout(() => scrollToBottom(), 0);
        return;
      }

      // Instantly resolve a pending optimistic placeholder in place when its
      // real twin arrives (matched by clientMessageId) so it doesn't linger on
      // "sending" until the reload lands. loadMessages below still runs and is
      // idempotent (mergeMessages dedups by id). Preserve _optimisticId for a
      // stable React key. mergeMessages also EVICTS orphaned placeholders on
      // reload, so a lost send response can never leave a permanent ghost.
      if (message && !isOptimisticMessage(message) && message.clientMessageId) {
        setMessages((prev) => {
          const placeholder = prev.find(
            (m) => isOptimisticMessage(m) && (m as OptimisticMessage).clientMessageId === message.clientMessageId
          ) as OptimisticMessage | undefined;
          if (!placeholder) return prev;
          return prev.map((m) =>
            isOptimisticMessage(m) && (m as OptimisticMessage)._optimisticId === placeholder._optimisticId
              ? ({ ...message, _optimisticId: placeholder._optimisticId } as Message)
              : m
          );
        });
        // Persist the resolved message so the offline cache (and the
        // loadMessages offline-fallback below, which replaces state from cache)
        // includes it — otherwise a network drop right after this resolve could
        // drop the just-resolved message from the list.
        if (currentThread.id) cacheMessages(String(currentThread.id), [message]).catch(() => {});
      }

      // Own real message — always scroll; incoming — only if near bottom
      const isOwnMessage =
        authUserRef.current && message && String(message.senderId) === String(authUserRef.current.id);
      if (isOwnMessage || nearBottom) setTimeout(() => scrollToBottom(), 0);
      loadMessages(currentThread.id);
      // Auto-mark read if user is near the bottom of the chat
      if (nearBottom && message && message.id) {
        lastMarkedIdRef.current = message.id;
        markRead(message).catch(() => {});
        setShowUnread(false);
        setMinUnreadMessageId(undefined);
      }
    };
    const onOptimisticResolve = (payload?: { optimisticId: string; message: Message | null; status: string }) => {
      const { optimisticId, message, status } = payload || {};
      if (!optimisticId) return;
      if (status === "sent" && message) {
        // Replace optimistic message with real server message — no full reload to avoid jank.
        // Preserve `_optimisticId` so the React key in MessagesListView stays stable
        // across the placeholder→real swap (prevents Virtuoso bubble remount + the
        // ~1s scroll-up jump where own messages disappear from view after sending).
        setMessages((prev) =>
          prev.map((m) =>
            isOptimisticMessage(m) && m._optimisticId === optimisticId ? { ...message, _optimisticId: optimisticId } : m
          )
        );
      } else if (status === "failed") {
        setMessages((prev) =>
          prev.map((m) =>
            isOptimisticMessage(m) && m._optimisticId === optimisticId ? { ...m, _status: "failed" as const } : m
          )
        );
      }
    };
    emitter.on(EVENTS.MESSAGES_NEW_MESSAGE, onNewMessage);
    emitter.on(EVENTS.MESSAGES_OPTIMISTIC_RESOLVE, onOptimisticResolve);
    return () => {
      emitter.off(EVENTS.MESSAGES_NEW_MESSAGE, onNewMessage);
      emitter.off(EVENTS.MESSAGES_OPTIMISTIC_RESOLVE, onOptimisticResolve);
    };
  }, [loadMessages, markRead, isNearBottom, scrollToBottom]);

  // Thread-specific live subscription. The app-wide `subscribeToAllThreads`
  // in PushManager is a wildcard (threadId="0") aggregator whose backend
  // stream skips certain message kinds — in particular the `__SYSTEM__:`
  // messages we emit for "Video call started / ended" and for group
  // joined/left/renamed events. As a result, anyone viewing the chat only
  // sees those entries after a leave+re-enter (which re-fetches from DB).
  //
  // A direct subscription to the specific thread ID receives EVERYTHING
  // the backend publishes for that thread including system messages.
  // We re-emit to MESSAGES_NEW_MESSAGE so the existing `onNewMessage`
  // handler (and all other listeners — MessageThreadList counters,
  // unread badges, etc.) still run. The handler's loadMessages() path
  // dedupes by thread id at the controller level and the reload from
  // server is idempotent, so double-fires from the two subscriptions
  // are harmless — only the backend-dropped system messages actually
  // produce a second event we wouldn't otherwise get.
  useEffect(() => {
    if (!thread?.id) return;
    let sub: { unsubscribe: () => void } | null = null;
    let cancelled = false;
    (async () => {
      try {
        const s = await providers.MessagesProvider.subscribeToThread(thread.id, (message: Message) => {
          emitter.emit(EVENTS.MESSAGES_NEW_MESSAGE, message);
        });
        if (cancelled) {
          s.unsubscribe();
          return;
        }
        sub = s;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[group-chat] subscribeToThread failed:", e);
      }
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [thread?.id]);

  // Live "typing…" receive path (the send path already exists via sendTyping).
  // The backend filters out the current user, so every event is a real peer.
  useEffect(() => {
    if (!thread?.id) return;
    let sub: { unsubscribe: () => void } | null = null;
    let cancelled = false;
    (async () => {
      try {
        const s = await providers.MessagesProvider.subscribeTypingIndicator(thread.id, (data) => {
          if (data?.userId) addTypingUser({ userId: String(data.userId), userName: data.userName || "" });
        });
        if (cancelled) {
          s.unsubscribe();
          return;
        }
        sub = s;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[group-chat] subscribeTypingIndicator failed:", e);
      }
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [thread?.id, addTypingUser]);

  // Latest messages, read inside loadMore to derive the scroll-up cursor
  // without recreating the callback on every message change.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const loadMore = useCallback(
    async (cb?: () => void) => {
      if (!thread || !hasMore) return;
      // Virtuoso's firstItemIndex now keeps scroll anchored on prepend — no
      // manual keep-scroll needed (it used to fight Virtuoso here).
      await loadMessages(thread.id, oldestRealId(messagesRef.current));
      cb?.();
    },
    [thread, hasMore, loadMessages]
  );

  // --- Jump-to-message (reply-quote click / global-search result / pins) ---
  // Pages older history (cursor loop over loadMessages' merged return value)
  // until the target is loaded; the shared hook handles router-state capture,
  // stale-navigation guards and the highlight pulse.
  const threadIdRef = useRef(thread?.id);
  threadIdRef.current = thread?.id;
  const loadUntilInGroup = useCallback(
    async (messageId: string): Promise<boolean> => {
      const tid = threadIdRef.current;
      if (!tid) return false;
      const has = (list: Message[]) => list.some((m) => String(m.id) === String(messageId));
      const MAX_JUMP_PAGES = 15;
      let list = messagesRef.current;
      for (let i = 0; i < MAX_JUMP_PAGES && !has(list); i++) {
        // Bail out if the user switched threads mid-loop — paging the OLD
        // thread would merge foreign messages into the new chat's state.
        if (threadIdRef.current !== tid) return false;
        const oldest = oldestRealId(list);
        // Target id sits inside the loaded range but isn't there — deleted.
        // (Assumes the loaded window is contiguous; a rare cache-hydration
        // gap can misclassify a gap message as deleted — the jump then
        // simply doesn't scroll.)
        if (oldest && Number(messageId) > Number(oldest)) return false;
        const merged = await loadMessages(tid, oldest);
        // A successful older-page load only ever GROWS the list. `<=` covers
        // both real exhaustion (equal length) AND a transient fetch error
        // (loadMessages returns [] on failure): without this, list would be
        // overwritten with [], oldestRealId([]) becomes undefined, and the next
        // iteration re-fetches the NEWEST page — spinning until the cap.
        if (merged.length <= list.length) return has(merged);
        list = merged;
      }
      return has(list);
    },
    [loadMessages]
  );

  const topicsOverviewActive = !!(group?.topicsEnabled && !topicId);
  const { jumpHighlightId, jumpToMessage, pendingJumpRef } = useJumpToMessage({
    chatKey: cacheKey,
    ready: !loading && !!thread && messages.length > 0 && !topicsOverviewActive,
    loadUntil: loadUntilInGroup,
  });

  // Active highlight: search takes priority over mention and jump
  const activeHighlightId = searchHighlightId || highlightMentionId || jumpHighlightId;

  // A search jump into a topics-enabled group lands on the Topics overview —
  // global search only surfaces General-thread messages (topic threads are
  // excluded from the thread list), so forward the jump to the General topic.
  // `forwardStateJumpId` is a dep so a jump pushed while ALREADY viewing this
  // group's overview (same URL, only location.state changes) still forwards.
  const location = useLocation();
  const forwardStateJumpId = (location.state as { highlightMessageId?: string } | undefined)?.highlightMessageId;
  useEffect(() => {
    if (!group?.topicsEnabled || topicId) return;
    const pending = pendingJumpRef.current;
    if (!pending) return;
    // Do NOT clear pendingJumpRef here: if the current location.state already
    // carries this same highlightMessageId, the replace below leaves stateJumpId
    // UNCHANGED, so the capture effect never re-populates the ref — clearing it
    // would strand the jump. The guard above (topicId set after the replace)
    // prevents a re-forward, and useJumpToMessage clears the ref itself once the
    // jump lands on the General topic.
    history.replace(generatePath(Routes.groupMessageTopic, { groupId: group.slug || group.id, topicId: "general" }), {
      highlightMessageId: pending,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.topicsEnabled, group?.slug, group?.id, topicId, history, forwardStateJumpId]);

  // Ctrl/Cmd+F to toggle search (second press restores native browser search)
  const searchOpenRef = useRef(searchOpen);
  searchOpenRef.current = searchOpen;
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        if (!searchOpenRef.current) {
          e.preventDefault();
          setSearchOpen(true);
        }
        // If already open, let native browser search through
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const handleSearch = useCallback(
    (query: string) => {
      search(query, messages);
    },
    [search, messages]
  );

  const handleCloseSearch = useCallback(() => {
    setSearchOpen(false);
    clearSearch();
  }, [clearSearch]);

  // --- Pinned messages ---
  const loadPinnedMessages = useCallback(async (tid: string) => {
    try {
      const json = await gqlFetch(
        `query GetPinned($threadId: ID!) { pinnedMessages(threadId: $threadId) { messageId message { id text senderUser { screenName } } pinnedBy pinnedTime } }`,
        { threadId: tid }
      );
      const pins = json?.data?.pinnedMessages || [];
      setPinnedMessages(
        pins.map((p: any) => ({
          messageId: p.messageId,
          text: p.message?.text || "",
          senderName: p.message?.senderUser?.screenName || "",
        }))
      );
      setPinnedIds(new Set(pins.map((p: any) => String(p.messageId))));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (thread?.id) loadPinnedMessages(thread.id);
  }, [thread?.id, loadPinnedMessages]);

  const handleTogglePin = useCallback(
    async (message: Message) => {
      if (!thread?.id) return;
      try {
        await gqlFetch(
          `mutation TogglePin($messageId: ID!, $threadId: ID!) { togglePinMessage(messageId: $messageId, threadId: $threadId) }`,
          { messageId: message.id, threadId: thread.id }
        );
        await loadPinnedMessages(thread.id);
      } catch {
        /* ignore */
      }
    },
    [thread?.id, loadPinnedMessages]
  );

  const toggleInfoPanel = useCallback(() => {
    setShowInfoPanel((prev) => !prev);
    setShowMediaPanel(false);
  }, []);

  const toggleMediaPanel = useCallback(() => {
    setShowMediaPanel((prev) => !prev);
    setShowInfoPanel(false);
  }, []);

  const handleMediaScrollToMessage = useCallback((messageId: string) => {
    setHighlightMentionId(messageId);
    setTimeout(() => setHighlightMentionId(undefined), 3000);
  }, []);

  const handleUnpin = useCallback(
    async (messageId: string) => {
      if (!thread?.id) return;
      try {
        await gqlFetch(
          `mutation UnpinMsg($messageId: ID!, $threadId: ID!) { togglePinMessage(messageId: $messageId, threadId: $threadId) }`,
          { messageId, threadId: thread.id }
        );
        await loadPinnedMessages(thread.id);
      } catch {
        /* ignore */
      }
    },
    [thread?.id, loadPinnedMessages]
  );

  const isGroupOwner = group && authUser && String(group.ownerId) === String(authUser.id);

  // Multi-select state — shared controller lives in useMessageSelection so
  // 1-on-1 and group chats behave identically. Exits itself on successful
  // bulk delete; partial failures keep the selection for user retry.
  const selection = useMessageSelection({
    messages,
    authUserId: authUser?.id,
    canModerate: !!isGroupOwner,
    onDeleted: (ids) => {
      const removed = new Set(ids);
      setMessages((prev) => prev.filter((m) => !removed.has(m.id)));
    },
  });

  const handleBulkDelete = useCallback(async () => {
    if (!window.confirm(t("Delete {{count}} messages?", { count: selection.selectedCount }))) return;
    const { failed } = await selection.deleteSelected();
    if (failed.length > 0) {
      // eslint-disable-next-line no-alert
      alert(t("Failed to delete {{count}} message(s).", { count: failed.length }));
    }
  }, [selection, t]);

  if (loading) {
    return (
      <div className="group-messages-list">
        <div className="group-messages-list__main">
          <ChatSkeleton showHeader />
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="group-messages-list">
        <div className="group-messages-list__empty-text">{t("Group not found")}</div>
      </div>
    );
  }

  // Group Topics (Темы) overview: a topics-enabled group opened without a topic
  // shows the list of topics instead of a chat. Tapping a topic routes to
  // /messages/group/:groupId/topic/:topicId.
  if (group.topicsEnabled && !topicId) {
    return (
      <div className="group-messages-list">
        <div className="group-messages-list__main">
          <TopicsList group={group} onBack={onBack} onOpenSettings={() => setShowInfoPanel(true)} />
        </div>
        {showInfoPanel && (
          <div className="group-messages-list__info-panel">
            <GroupInfoPanel
              group={group}
              threadId={thread?.id}
              onClose={() => setShowInfoPanel(false)}
              onMembersChanged={(updated) => setGroup(updated)}
              onLeaveGroup={() => history.push("/messages")}
              onDeleteGroup={() => history.push("/messages")}
            />
          </div>
        )}
      </div>
    );
  }

  // In a topic, the header back button returns to the topics overview rather than
  // the chat list (one extra navigation level).
  const headerBack = topicId
    ? () => history.push(generatePath(Routes.groupMessages, { groupId: group.slug || group.id }))
    : onBack;

  return (
    <div className="group-messages-list">
      <div className="group-messages-list__main">
        {selection.selectionMode ? (
          <ChatSelectionToolbar
            count={selection.selectedCount}
            canDeleteAny={selection.canDeleteAny}
            onDelete={handleBulkDelete}
            onForward={() => setForwardBulk(selection.selectedMessages)}
            onCancel={selection.cancel}
          />
        ) : (
          <div onClick={() => !searchOpen && toggleInfoPanel()} style={{ cursor: "pointer" }}>
            <GroupChatHeader
              group={group}
              threadId={thread?.id}
              topicName={topicName}
              onBack={headerBack}
              onStartCall={handleStartCall}
              onStartVoiceCall={handleStartVoiceCall}
              activeCall={!!activeCallUuid}
              onToggleSearch={() => setSearchOpen(!searchOpen)}
              searchOpen={searchOpen}
              typingUsers={typingUsers}
              onToggleMedia={thread?.id ? toggleMediaPanel : undefined}
              mediaOpen={showMediaPanel}
            />
          </div>
        )}
        {searchOpen && !selection.selectionMode && (
          <ChatSearchBar
            onSearch={handleSearch}
            onNavigate={searchNavigate}
            onClose={handleCloseSearch}
            currentIndex={searchIndex}
            totalResults={searchResults.length}
          />
        )}
        {!searchOpen && pinnedMessages.length > 0 && (
          <PinnedMessagesBar
            pinnedMessages={pinnedMessages}
            onScrollToMessage={jumpToMessage}
            onUnpin={handleUnpin}
            canUnpin={!!isGroupOwner}
          />
        )}
        {messages.length > 0 && (
          <MessagesListView
            avatarSize={36}
            containerRef={reverseContentRef}
            scrollToBottomRef={scrollToBottomFnRef}
            settleAtBottomRef={settleAtBottomFnRef}
            messages={messages}
            infiniteScrollEnabled={hasMore}
            loadMore={loadMore}
            showScreenName={true}
            showUnread={showUnread && !searchOpen}
            minUnreadMessageId={minUnreadMessageId}
            highlightMessageId={activeHighlightId}
            searchQuery={searchOpen ? searchQuery : undefined}
            onMessageUpdated={(updated) => {
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== updated.id) return m;
                  // Preserve `_optimisticId` so MessagesListView's stable
                  // React key doesn't flip on edit of a just-sent message.
                  const prevOpt = (m as { _optimisticId?: string })._optimisticId;
                  return prevOpt ? { ...updated, _optimisticId: prevOpt } : updated;
                })
              );
              // Persist the edit so a reload doesn't hydrate the stale pre-edit
              // copy from IndexedDB (original text flashes for ~1s otherwise).
              if (thread?.id) cacheMessages(String(thread.id), [updated]).catch(() => {});
            }}
            onMessageDeleted={(messageId) => {
              setMessages((prev) => prev.filter((m) => m.id !== messageId));
              // Purge from IndexedDB too — otherwise the cached copy
              // resurrects the message after a page reload.
              removeCachedMessage(messageId).catch(() => {});
            }}
            hasMention={thread?.hasMention}
            onMentionClick={scrollToMention}
            onReply={setReplyToMessage}
            onForward={setForwardMessage}
            onPin={isGroupOwner ? handleTogglePin : undefined}
            onOpenThread={setThreadParentMessage}
            onQuoteClick={jumpToMessage}
            threadReplyCounts={threadReplyCounts}
            pinnedMessageIds={pinnedIds}
            activeCallUuid={activeCallUuid}
            canModerate={!!isGroupOwner}
            selectionMode={selection.selectionMode}
            selectedIds={selection.selectedIds}
            onToggleSelection={selection.toggle}
            onEnterSelection={(msg) => selection.enter(msg)}
          />
        )}
        {messages.length === 0 && !loading && (
          <div className="group-messages-list__empty-text">{t("No messages in this group yet")}</div>
        )}
        {group.isChannel && String(group.ownerId) !== String(authUser?.id) ? (
          <div className="group-messages-list__channel-readonly">{t("Only the channel owner can post here")}</div>
        ) : thread ? (
          // Composer only with a resolved thread: without threadIds,
          // buildMessageData falls back to recipientIds=memberIds and a group
          // send would fan out as N personal DMs (the exact bug the
          // recipientIds/threadIds mutual exclusion was added to close). Thread
          // resolution failing after all retries leaves a broken view anyway —
          // better no composer than a misrouting one.
          <>
            <ScheduledMessages threadId={String(thread.id)} />
            <MessageInput
              threadIds={[thread.id]}
              userIds={group.memberIds}
              replyToMessage={replyToMessage}
              onClearReply={() => setReplyToMessage(null)}
              onTyping={() => sendTyping()}
              hideTip={!!group.isChannel}
            />
          </>
        ) : null}
      </div>
      {threadParentMessage && thread && authUser && (
        <ThreadPanel
          parentMessage={threadParentMessage}
          authUser={authUser}
          threadId={String(thread.id)}
          onClose={() => setThreadParentMessage(null)}
        />
      )}
      {showInfoPanel && (
        <div className="group-messages-list__info-panel">
          <GroupInfoPanel
            group={group}
            threadId={thread?.id}
            onClose={() => setShowInfoPanel(false)}
            onMembersChanged={(updated) => setGroup(updated)}
            onLeaveGroup={() => history.push("/messages")}
            onDeleteGroup={() => history.push("/messages")}
          />
        </div>
      )}
      {showMediaPanel && thread?.id && (
        <div className="group-messages-list__info-panel">
          <MediaGalleryPanel
            threadId={thread.id}
            onClose={() => setShowMediaPanel(false)}
            onScrollToMessage={handleMediaScrollToMessage}
          />
        </div>
      )}
      {forwardMessage && <ForwardMessageModal messages={[forwardMessage]} onClose={() => setForwardMessage(null)} />}
      {forwardBulk && forwardBulk.length > 0 && (
        <ForwardMessageModal
          messages={forwardBulk}
          onClose={() => {
            setForwardBulk(null);
            selection.cancel();
          }}
        />
      )}
    </div>
  );
}

export default GroupMessagesList;
